import React from 'react';
import {
  AlertTriangle, Download, Loader2, Target, Zap, HelpCircle,
} from 'lucide-react';
import * as api from '@/api/client';
import type { QaQuestion, Scores } from '@/types';

import { apiErrorText } from '../../../lib/describeApiError';
export type AnswerShape = { selected_option?: string; free_text: string };

export type AnswersMap = Record<number, AnswerShape>;

/** Count answered entries in the shared `answers` map. Lives here so
 *  both the submit button (inside this component) and the TabBar
 *  badge (in the parent) compute identical values — the parent
 *  imports + calls this helper to derive the unanswered count. */
export function countAnswered(answers: AnswersMap): number {
  return (Object.values(answers) as AnswerShape[]).filter(
    (a) => a.selected_option || (a.free_text || '').trim(),
  ).length;
}

/** Per-parameter score ceiling; mirrors _MAX_PARAM_SCORE in server.py. */
export const MAX_PARAM_SCORE = 92;

/** Unapplied answers are kept in sessionStorage so leaving Stage 1
 *  (which unmounts it) doesn't lose them. Keyed per matter and per
 *  Q&A round id: each generate inserts a new round, so a draft never
 *  attaches to a different question set. Storage can throw (private
 *  browsing, quota); that just means no draft. */
export function qaDraftKey(matterId: string, roundId: string): string {
  return `stage1_qa_draft_${matterId}_${roundId}`;
}

export function readQaDraft(matterId: string, roundId: string): AnswersMap | null {
  if (!matterId || !roundId) return null;
  try {
    const stored = window.sessionStorage.getItem(qaDraftKey(matterId, roundId));
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0)
      ? parsed
      : null;
  } catch { return null; }
}

export function writeQaDraft(matterId: string, roundId: string, answers: AnswersMap): void {
  if (!matterId || !roundId) return;
  try {
    window.sessionStorage.setItem(qaDraftKey(matterId, roundId), JSON.stringify(answers));
  } catch { /* private mode / quota — fall through */ }
}

export function clearQaDraft(matterId: string, roundId: string): void {
  if (!matterId || !roundId) return;
  try {
    window.sessionStorage.removeItem(qaDraftKey(matterId, roundId));
  } catch { /* private mode — fall through */ }
}

interface SmartQATabProps {
  matterId: string;
  matterTitle: string;
  scores: Scores;
  hasScores: boolean;
  isFrozen: boolean;
  generatingQa: boolean;
  submittingAnswers: boolean;
  /** True after a successful submitAnswers call until the drafter
   *  modifies an answer. Drives the Apply button's
   *  "✓ Answers applied" terminal state and disables the button
   *  to block redundant re-submits. */
  answersApplied: boolean;
  currentQa: {
    round_number: number;
    questions: QaQuestion[];
    empty_reason?: string | null;
  } | null;
  answers: AnswersMap;
  setAnswers: React.Dispatch<React.SetStateAction<AnswersMap>>;
  handleGenerateQa: () => Promise<void> | void;
  handleSubmitAnswers: () => Promise<void> | void;
  setError: (msg: string | null) => void;
}

/**
 * Smart Q&A tab — pure relocation of the Q&A card that previously
 * lived in the grid-left column of Stage 1. Every element
 * (Generate/Regenerate, DOCX export, gamified parameter banner,
 * target-improvement hint, per-question textarea, chip options,
 * submit button, empty-state callout) moves verbatim; the outer
 * `hasScores && !isFrozen` gate is preserved so the tab stays blank
 * for pre-scoring / frozen matters just like the old card did.
 */
export default function SmartQATab({
  matterId,
  matterTitle,
  scores,
  hasScores,
  isFrozen,
  generatingQa,
  submittingAnswers,
  answersApplied,
  currentQa,
  answers,
  setAnswers,
  handleGenerateQa,
  handleSubmitAnswers,
  setError,
}: SmartQATabProps) {
  // Tracks whether the LAST generate attempt failed — drives the
  // button label from "Generate Questions" → "Retry" + switches the
  // empty-state copy to a failure-aware sentence. React-local state
  // only; resets on successful regeneration because the parent flips
  // currentQa to a non-null value.
  const [qaFailed, setQaFailed] = React.useState(false);
  const wrappedGenerate = async () => {
    setQaFailed(false);
    try {
      await handleGenerateQa();
    } catch {
      setQaFailed(true);
    }
  };

  // Frozen: Q&A becomes read-only metadata only, keep the pre-existing
  // one-liner. Matches the stage_2 "frozen" pattern across the app.
  if (isFrozen) {
    return (
      <div
        data-testid="qa-section-empty-preconditions"
        className="text-xs text-zinc-500 italic p-4"
      >
        Stage 1 is frozen — Q&amp;A is read-only after freeze.
      </div>
    );
  }

  // Scoring hasn't run yet (or returned empty). Explicit card with a
  // clear next step + disabled Generate button so the drafter
  // understands the precondition without having to read the left rail.
  if (!hasScores) {
    return (
      <div
        data-testid="qa-section-needs-score"
        className="border border-zinc-200 rounded-sm bg-white p-6 text-sm text-zinc-500"
      >
        <div className="flex items-center gap-2 mb-2 text-zinc-700">
          <AlertTriangle size={14} className="text-amber-600" />
          <span className="font-medium">Score your IDF first</span>
        </div>
        <p className="text-xs leading-relaxed mb-3">
          Analyze your IDF to get completeness scores before generating
          questions. Q&amp;A is driven by which parameters scored low
          enough to warrant targeted clarification.
        </p>
        <button
          type="button"
          data-testid="qa-generate-disabled"
          disabled
          title="Run Analyze IDF on the left panel first"
          className="bg-zinc-100 text-zinc-400 rounded-sm px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 cursor-not-allowed"
        >
          <Zap size={12} /> Generate Questions
        </button>
      </div>
    );
  }

  return (
    <div className="border border-zinc-200 rounded-sm bg-white p-4" data-testid="qa-section">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500">
          Smart Q&amp;A
        </h3>
        <div className="flex items-center gap-1.5">
          {currentQa && currentQa.questions.length > 0 && (
            <button
              data-testid="qa-download-docx-button"
              onClick={async () => {
                setError(null);
                try {
                  await api.downloadQaQuestionnaire(matterId, matterTitle || 'matter');
                } catch (e: any) {
                  setError(apiErrorText(e, 'Questionnaire download failed'));
                }
              }}
              title="Download as DOCX to share with the inventor"
              className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-2.5 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5"
            >
              <Download size={12} />
              DOCX
            </button>
          )}
          <button
            data-testid="generate-qa-button"
            onClick={wrappedGenerate}
            disabled={generatingQa}
            className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50"
          >
            {generatingQa
              ? <Loader2 size={12} className="animate-spin" />
              : <Zap size={12} />}
            {generatingQa
              ? 'Generating…'
              : qaFailed
                ? 'Retry'
                : currentQa ? 'Re-generate' : 'Generate Questions'}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-zinc-400 mb-3">
        Optional — answer any, all, or none. Each question shows the current
        completeness score for that parameter and what would push it to {MAX_PARAM_SCORE}%.
        Prior art is not asked (v1).
      </p>

      {!currentQa && !generatingQa && (
        <div
          data-testid="qa-section-empty"
          className="rounded-sm border border-zinc-100 bg-zinc-50 p-4"
        >
          <div className="flex items-center gap-2 text-zinc-700 mb-1">
            <HelpCircle size={14} className="text-[#002FA7]" />
            <span className="text-sm font-medium">
              {qaFailed ? 'Generation failed' : 'Questions not yet generated'}
            </span>
          </div>
          <p className="text-xs text-zinc-600 leading-relaxed">
            {qaFailed
              ? 'The last attempt didn\'t produce a usable question set. Click Retry above — the generator re-runs against the same scores.'
              : 'Based on your completeness scores, the model will suggest targeted questions to close the lowest-scoring gaps. Click Generate Questions to start.'}
          </p>
        </div>
      )}

      {/* Empty state: call succeeded but the backend produced no
          questions (LLM returned an empty set, refused, or parse
          failed). Surface the reason the server sent back so the
          drafter knows whether to regenerate, reword their notes,
          or check the LLM connection. */}
      {currentQa && currentQa.questions.length === 0 && !generatingQa && (
        <div
          data-testid="qa-empty-state"
          className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900 flex items-start gap-2"
        >
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-amber-600" />
          <div>
            <div className="font-medium mb-1">No questions were produced</div>
            <div>
              {currentQa.empty_reason ||
                'The model returned an empty set. Try Regenerate, or add a nudge in Your notes and re-analyse the IDF.'}
            </div>
          </div>
        </div>
      )}

      {currentQa && currentQa.questions.length > 0 && (
        <div className="space-y-4">
          {currentQa.questions.map((q, i) => {
            const paramLabel = (
              q.parameter === 'technical_effect' ? 'Tech Effect'
                : q.parameter === 'problem' ? 'Problem'
                : q.parameter === 'solution' ? 'Solution'
                : q.parameter === 'embodiments' ? 'Embodiments'
                : q.parameter || 'General'
            );
            const current = typeof q.current_score === 'number'
              ? q.current_score
              : (scores as any)[q.parameter] || 0;
            const gap = Math.max(0, MAX_PARAM_SCORE - current);
            return (
              <div key={i} className="pb-3 border-b border-zinc-100 last:border-b-0 last:pb-0" data-testid={`qa-question-${i}`}>
                {/* Gamified banner — current score → parameter ceiling */}
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="px-1.5 py-0.5 bg-blue-50 border border-blue-200 rounded-sm text-[10px] text-[#002FA7] font-mono uppercase tracking-wide">
                    {paramLabel}
                  </span>
                  <span className="font-mono text-[11px] text-zinc-600">
                    {current}% <span className="text-zinc-400">→ {MAX_PARAM_SCORE}%</span>
                  </span>
                  <span className="text-[10px] text-green-700 font-medium ml-auto">+{gap} pts available</span>
                </div>
                {/* Target improvement hint */}
                {q.target_improvement && (
                  <div className="flex items-start gap-1.5 mb-1.5 text-[11px] text-zinc-600 italic">
                    <Target size={11} className="mt-0.5 flex-shrink-0 text-green-600" />
                    <span>{q.target_improvement}</span>
                  </div>
                )}
                <p className="text-xs text-zinc-900 leading-snug mb-2">{q.question}</p>

                {/* Primary interaction: a technical free-text box. The
                    inventor writes numbers, ranges, materials — not a
                    quick chip pick. Chips below are secondary hints. */}
                <textarea
                  data-testid={`qa-freetext-${i}`}
                  placeholder="Write technical detail — numbers, ranges, materials, conditions…"
                  value={answers[i]?.free_text || ''}
                  onChange={(e) => setAnswers(prev => ({
                    ...prev,
                    [i]: {
                      selected_option: prev[i]?.selected_option,
                      free_text: e.target.value,
                    },
                  }))}
                  rows={3}
                  className="w-full bg-white border border-zinc-200 rounded-sm px-2 py-1.5 text-xs leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#002FA7] resize-y min-h-[4.5rem] mb-1.5"
                />

                {/* Indicative answer hints — smaller, secondary. */}
                {q.options.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 text-[10px]">
                    <span className="text-zinc-400 font-mono uppercase tracking-wide mr-1">
                      Possible directions:
                    </span>
                    {q.options.map((opt, oi) => {
                      const selected = answers[i]?.selected_option === opt;
                      return (
                        <button
                          key={oi}
                          data-testid={`qa-chip-${i}-${oi}`}
                          onClick={() => setAnswers(prev => ({
                            ...prev,
                            [i]: {
                              selected_option: selected ? undefined : opt,
                              free_text: prev[i]?.free_text || '',
                            },
                          }))}
                          className={`px-2 py-0.5 rounded-full border transition-colors ${
                            selected
                              ? 'border-[#002FA7] bg-blue-50 text-[#002FA7]'
                              : 'border-zinc-300 bg-white text-zinc-600 hover:border-[#002FA7]'
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {(() => {
            const answeredCount = countAnswered(answers);
            // Three terminal states for the Apply button:
            //   submittingAnswers — request in flight, spinner.
            //   answersApplied    — last successful submit's answer
            //                       set is still in the textareas;
            //                       block the resubmit and surface a
            //                       confirmation. Wrapped setAnswers
            //                       in Stage1.tsx clears this on any
            //                       drafter edit.
            //   default           — at least one answer typed,
            //                       button enabled.
            const disabled =
              submittingAnswers
              || answersApplied
              || answeredCount === 0;
            const label = submittingAnswers
              ? 'Applying…'
              : answersApplied
                ? '✓ Answers applied'
                : answeredCount === 0
                  ? 'Apply Answers (none picked yet)'
                  : `Apply ${answeredCount} Answer${answeredCount === 1 ? '' : 's'}`;
            const cls = answersApplied
              ? 'w-full bg-green-50 text-green-800 border border-green-200 rounded-sm px-3 py-2 text-xs font-medium flex items-center justify-center gap-2 cursor-not-allowed'
              : 'w-full bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-3 py-2 text-xs font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-40';
            return (
              <button
                data-testid="submit-answers-button"
                data-applied={answersApplied ? 'true' : 'false'}
                onClick={handleSubmitAnswers}
                disabled={disabled}
                className={cls}
              >
                {submittingAnswers && <Loader2 size={12} className="animate-spin" />}
                {label}
              </button>
            );
          })()}
        </div>
      )}
    </div>
  );
}
